import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import { 
  TrendingUp, DollarSign, Car, Clock, Download, FileSpreadsheet, 
  FileText, Calendar, RefreshCw 
} from 'lucide-react';

const API = '/api';

const COLORS = ['#302681', '#009440', '#FFCB00', '#FF6B6B', '#4ECDC4', '#45B7D1'];

const AnalyticsDashboard = ({ authToken }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/owner/analytics`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end
        }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erro ao carregar analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) {
      fetchAnalytics();
    }
  }, [authToken]);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/owner/reports/excel`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_parkingspot_${dateRange.start}_${dateRange.end}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Relatório Excel exportado com sucesso!');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Erro ao exportar Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/owner/reports/pdf`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_parkingspot_${dateRange.start}_${dateRange.end}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Relatório PDF exportado com sucesso!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erro ao exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#302681]"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <Card className="p-12 text-center">
        <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-400" />
        <h3 className="text-xl font-semibold mb-2">Sem dados de analytics</h3>
        <p className="text-gray-600">Não há dados disponíveis para o período selecionado</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Date Filter and Export */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <TrendingUp className="w-6 h-6 text-[#302681]" />
            <span>Analytics & Relatórios</span>
          </h2>
          <p className="text-gray-600 text-sm mt-1">Visualize métricas e exporte relatórios</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <Input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-36"
              data-testid="analytics-start-date"
            />
            <span className="text-gray-500">a</span>
            <Input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-36"
              data-testid="analytics-end-date"
            />
          </div>
          
          <Button
            onClick={fetchAnalytics}
            variant="outline"
            disabled={loading}
            data-testid="analytics-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={handleExportExcel}
          disabled={exporting}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="export-excel-btn"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Exportar Excel
        </Button>
        <Button
          onClick={handleExportPDF}
          disabled={exporting}
          className="bg-red-600 hover:bg-red-700 text-white"
          data-testid="export-pdf-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          Exportar PDF
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 bg-gradient-to-br from-[#302681]/10 to-[#302681]/5 border-[#302681]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Receita Total</p>
              <p className="text-2xl font-bold text-[#302681]" data-testid="total-revenue">
                {formatCurrency(analytics.summary.total_revenue)}
              </p>
            </div>
            <div className="p-3 bg-[#302681]/10 rounded-full">
              <DollarSign className="w-6 h-6 text-[#302681]" />
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-green-50 to-green-100/50 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total de Veículos</p>
              <p className="text-2xl font-bold text-green-700" data-testid="total-vehicles">
                {analytics.summary.total_vehicles}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Car className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Tempo Médio</p>
              <p className="text-2xl font-bold text-amber-700" data-testid="avg-duration">
                {formatDuration(analytics.summary.avg_duration_minutes)}
              </p>
            </div>
            <div className="p-3 bg-amber-100 rounded-full">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Ticket Médio</p>
              <p className="text-2xl font-bold text-blue-700" data-testid="avg-ticket">
                {formatCurrency(analytics.summary.avg_ticket)}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Day */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-[#302681]" />
            <span>Receita por Dia</span>
          </h3>
          {analytics.revenue_by_day.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={analytics.revenue_by_day}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#302681" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#302681" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => `R$${value}`}
                />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), 'Receita']}
                  labelFormatter={(label) => {
                    const date = new Date(label);
                    return date.toLocaleDateString('pt-BR');
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#302681" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorRevenue)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              Sem dados de receita para o período
            </div>
          )}
        </Card>

        {/* Occupancy by Hour */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center space-x-2">
            <Clock className="w-5 h-5 text-green-600" />
            <span>Horários de Pico (Check-ins)</span>
          </h3>
          {analytics.occupancy_by_hour.some(h => h.checkins > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.occupancy_by_hour}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="hour" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => `${value}h`}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip 
                  formatter={(value) => [value, 'Check-ins']}
                  labelFormatter={(label) => `${label}:00`}
                />
                <Bar 
                  dataKey="checkins" 
                  fill="#009440" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              Sem dados de check-in para o período
            </div>
          )}
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Parking Lot */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-[#302681]" />
            <span>Receita por Estacionamento</span>
          </h3>
          {analytics.revenue_by_lot.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.revenue_by_lot} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  type="number"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => `R$${value}`}
                />
                <YAxis 
                  type="category"
                  dataKey="lot_name" 
                  tick={{ fontSize: 11 }}
                  width={120}
                />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), 'Receita']}
                />
                <Bar 
                  dataKey="revenue" 
                  fill="#302681" 
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              Sem dados de estacionamentos
            </div>
          )}
        </Card>

        {/* Payment Methods */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center space-x-2">
            <Download className="w-5 h-5 text-amber-600" />
            <span>Métodos de Pagamento</span>
          </h3>
          {analytics.payment_methods.some(m => m.value > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analytics.payment_methods.filter(m => m.value > 0)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="method"
                >
                  {analytics.payment_methods.filter(m => m.value > 0).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), 'Valor']}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              Sem dados de pagamentos
            </div>
          )}
        </Card>
      </div>

      {/* Transactions Table */}
      {analytics.revenue_by_day.length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4">Transações por Dia</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-4 font-semibold">Data</th>
                  <th className="text-right py-3 px-4 font-semibold">Transações</th>
                  <th className="text-right py-3 px-4 font-semibold">Receita</th>
                </tr>
              </thead>
              <tbody>
                {analytics.revenue_by_day.slice(-10).reverse().map((day, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      {new Date(day.date).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="py-3 px-4 text-right">{day.transactions}</td>
                    <td className="py-3 px-4 text-right font-semibold text-green-700">
                      {formatCurrency(day.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
